/**
 * Jobs Financial View Page
 *
 * Displays all jobs with complete financial information including:
 * - Carteirado (budgeted value)
 * - Faturado (billed amount)
 * - Aguardando Pagamento (awaiting payment)
 * - Custos Realizados (realized costs)
 * - Custos Futuros (projected costs)
 * - Margens (Current and Projected margins)
 *
 * Features:
 * - Filters by client, supplier, status, period
 * - Summary cards with key metrics
 * - Sortable table with all financial data
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Briefcase,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Filter,
  Download,
  Calendar,
  Users,
  Building2
} from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { FinancialSkeleton, FinancialEmptyState } from '../financial/shared/components';
import { usePageTitle } from '@/stores/page-title-store';

// ============================================================================
// TYPES
// ============================================================================

interface Job {
  id: string;
  code: string;
  name: string;
  client: string;
  clientId: string;
  status: 'ativo' | 'concluido' | 'pausado' | 'cancelado';
  carteirado: number;
  faturado: number;
  aguardandoPagamento: number;
  custoAtual: number;
  custoFuturo: number;
  margemAtual: number;
  margemProjetada: number;
  startDate: string;
  endDate?: string;
}

type JobStatus = 'ativo' | 'concluido' | 'pausado' | 'cancelado' | 'todos';
type SortField = 'name' | 'client' | 'carteirado' | 'faturado' | 'custoAtual' | 'margemAtual' | 'margemProjetada';
type SortDirection = 'asc' | 'desc';

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_JOBS: Job[] = [
  {
    id: '1',
    code: 'JOB-2025-001',
    name: 'Campanha Verão 2025',
    client: 'Cliente A',
    clientId: 'CL-001',
    status: 'ativo',
    carteirado: 150000,
    faturado: 90000,
    aguardandoPagamento: 30000,
    custoAtual: 65000,
    custoFuturo: 25000,
    margemAtual: 27.78,
    margemProjetada: 40.00,
    startDate: '2025-01-15',
    endDate: '2025-03-30',
  },
  {
    id: '2',
    code: 'JOB-2025-002',
    name: 'Rebranding Corporativo',
    client: 'Cliente B',
    clientId: 'CL-002',
    status: 'ativo',
    carteirado: 280000,
    faturado: 180000,
    aguardandoPagamento: 50000,
    custoAtual: 120000,
    custoFuturo: 80000,
    margemAtual: 33.33,
    margemProjetada: 28.57,
    startDate: '2024-11-01',
    endDate: '2025-04-30',
  },
  {
    id: '3',
    code: 'JOB-2024-087',
    name: 'Lançamento Produto X',
    client: 'Cliente C',
    clientId: 'CL-003',
    status: 'concluido',
    carteirado: 95000,
    faturado: 95000,
    aguardandoPagamento: 0,
    custoAtual: 58000,
    custoFuturo: 0,
    margemAtual: 38.95,
    margemProjetada: 38.95,
    startDate: '2024-09-01',
    endDate: '2024-12-15',
  },
  {
    id: '4',
    code: 'JOB-2025-003',
    name: 'Evento Anual 2025',
    client: 'Cliente A',
    clientId: 'CL-001',
    status: 'ativo',
    carteirado: 420000,
    faturado: 150000,
    aguardandoPagamento: 100000,
    custoAtual: 95000,
    custoFuturo: 180000,
    margemAtual: 36.67,
    margemProjetada: 34.52,
    startDate: '2025-02-01',
    endDate: '2025-06-30',
  },
  {
    id: '5',
    code: 'JOB-2025-004',
    name: 'Conteúdo Digital Q1',
    client: 'Cliente D',
    clientId: 'CL-004',
    status: 'ativo',
    carteirado: 75000,
    faturado: 25000,
    aguardandoPagamento: 15000,
    custoAtual: 18000,
    custoFuturo: 32000,
    margemAtual: 28.00,
    margemProjetada: 33.33,
    startDate: '2025-01-01',
    endDate: '2025-03-31',
  },
  {
    id: '6',
    code: 'JOB-2024-092',
    name: 'Pesquisa de Mercado',
    client: 'Cliente E',
    clientId: 'CL-005',
    status: 'pausado',
    carteirado: 125000,
    faturado: 40000,
    aguardandoPagamento: 0,
    custoAtual: 35000,
    custoFuturo: 45000,
    margemAtual: 12.50,
    margemProjetada: 36.00,
    startDate: '2024-10-15',
  },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function getStatusLabel(status: JobStatus): string {
  const labels: Record<JobStatus, string> = {
    ativo: 'Ativo',
    concluido: 'Concluído',
    pausado: 'Pausado',
    cancelado: 'Cancelado',
    todos: 'Todos',
  };
  return labels[status];
}

function getStatusColor(status: Job['status']): string {
  const colors: Record<Job['status'], string> = {
    ativo: 'bg-green-500/10 text-green-400 border-green-500/20',
    concluido: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    pausado: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    cancelado: 'bg-red-500/10 text-red-400 border-red-500/20',
  };
  return colors[status];
}

// ============================================================================
// SUMMARY CARD COMPONENT
// ============================================================================

interface SummaryCardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  colorClass: string;
  subtitle?: string;
}

function SummaryCard({ label, value, icon: Icon, colorClass, subtitle }: SummaryCardProps) {
  return (
    <div className={`bg-gradient-to-br ${colorClass} p-6 rounded-monumenta border min-w-[180px]`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-brand-gray-light font-body">{label}</span>
        <Icon className="h-5 w-5 opacity-70" />
      </div>
      <div className="text-2xl sm:text-3xl font-bold font-display" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatCurrency(value)}
      </div>
      {subtitle && (
        <div className="text-xs mt-2 opacity-70">
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function JobsFinancialPage() {
  const navigate = useNavigate();
  usePageTitle('Jobs');

  // State
  const [jobs] = useState<Job[]>(MOCK_JOBS);
  const [loading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<JobStatus>('todos');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'carteirado', desc: true }]);

  // Get unique clients for filter dropdown
  const uniqueClients = useMemo(() => {
    const clients = Array.from(new Set(jobs.map(j => j.client))).sort();
    return clients;
  }, [jobs]);

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    let filtered = [...jobs];

    // Status filter
    if (statusFilter !== 'todos') {
      filtered = filtered.filter(j => j.status === statusFilter);
    }

    // Client filter
    if (clientFilter) {
      filtered = filtered.filter(j => j.client === clientFilter);
    }

    // Text search
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(j =>
        j.name.toLowerCase().includes(search) ||
        j.code.toLowerCase().includes(search) ||
        j.client.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [jobs, statusFilter, clientFilter, searchText]);

  // Calculate totals
  const totals = useMemo(() => {
    return filteredJobs.reduce(
      (acc, job) => ({
        carteirado: acc.carteirado + job.carteirado,
        faturado: acc.faturado + job.faturado,
        custosRealizados: acc.custosRealizados + job.custoAtual,
        custosFuturos: acc.custosFuturos + job.custoFuturo,
      }),
      { carteirado: 0, faturado: 0, custosRealizados: 0, custosFuturos: 0 }
    );
  }, [filteredJobs]);

  // Table columns
  const columnHelper = createColumnHelper<Job>();

  const columns = useMemo(() => [
    columnHelper.accessor('code', {
      header: 'Código',
      cell: (info) => (
        <div className="font-mono text-sm text-brand-gray-light">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('name', {
      header: 'Job',
      cell: (info) => {
        const job = info.row.original;
        return (
          <div>
            <div className="font-medium text-white">{info.getValue()}</div>
            <div className="text-xs text-brand-gray mt-0.5">{job.client}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: () => <div className="text-center">Status</div>,
      cell: (info) => (
        <div className="flex justify-center">
          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(info.getValue())}`}>
            {getStatusLabel(info.getValue())}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('carteirado', {
      header: () => <div className="text-right">Carteirado</div>,
      cell: (info) => (
        <div className="text-right font-mono text-sm text-blue-400">
          {formatCurrency(info.getValue())}
        </div>
      ),
    }),
    columnHelper.accessor('faturado', {
      header: () => <div className="text-right">Faturado</div>,
      cell: (info) => (
        <div className="text-right font-mono text-sm text-green-400">
          {formatCurrency(info.getValue())}
        </div>
      ),
    }),
    columnHelper.accessor('aguardandoPagamento', {
      header: () => <div className="text-right">Aguardando</div>,
      cell: (info) => (
        <div className="text-right font-mono text-sm text-yellow-400">
          {formatCurrency(info.getValue())}
        </div>
      ),
    }),
    columnHelper.accessor('custoAtual', {
      header: () => <div className="text-right">Custo Atual</div>,
      cell: (info) => (
        <div className="text-right font-mono text-sm text-orange-400">
          {formatCurrency(info.getValue())}
        </div>
      ),
    }),
    columnHelper.accessor('custoFuturo', {
      header: () => <div className="text-right">Custo Futuro</div>,
      cell: (info) => (
        <div className="text-right font-mono text-sm text-purple-400">
          {formatCurrency(info.getValue())}
        </div>
      ),
    }),
    columnHelper.accessor('margemAtual', {
      header: () => <div className="text-right">Margem Atual %</div>,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className={`text-right font-mono text-sm font-medium flex items-center justify-end gap-1 ${
            value >= 30 ? 'text-green-400' : value >= 20 ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {value >= 30 ? <TrendingUp className="h-3 w-3" /> : value < 20 ? <TrendingDown className="h-3 w-3" /> : null}
            {formatPercent(value)}
          </div>
        );
      },
    }),
    columnHelper.accessor('margemProjetada', {
      header: () => <div className="text-right">Margem Proj. %</div>,
      cell: (info) => {
        const value = info.getValue();
        const current = info.row.original.margemAtual;
        const improving = value > current;
        return (
          <div className={`text-right font-mono text-sm font-medium flex items-center justify-end gap-1 ${
            value >= 30 ? 'text-green-400' : value >= 20 ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {improving ? <TrendingUp className="h-3 w-3" /> : value < current ? <TrendingDown className="h-3 w-3" /> : null}
            {formatPercent(value)}
          </div>
        );
      },
    }),
  ], [columnHelper]);

  const table = useReactTable({
    data: filteredJobs,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Handle CSV export
  const handleExportCSV = () => {
    const headers = [
      'Código',
      'Nome',
      'Cliente',
      'Status',
      'Carteirado',
      'Faturado',
      'Aguardando',
      'Custo Atual',
      'Custo Futuro',
      'Margem Atual %',
      'Margem Projetada %',
    ];
    const rows = filteredJobs.map(j => [
      j.code,
      j.name,
      j.client,
      getStatusLabel(j.status),
      j.carteirado.toFixed(2),
      j.faturado.toFixed(2),
      j.aguardandoPagamento.toFixed(2),
      j.custoAtual.toFixed(2),
      j.custoFuturo.toFixed(2),
      j.margemAtual.toFixed(2),
      j.margemProjetada.toFixed(2),
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `jobs-financeiro-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <FinancialSkeleton variant="table" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Action Buttons */}
      <div className="flex justify-end">
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 bg-brand-dark-light text-brand-gray-light rounded-monumenta border border-gray-800/50 hover:bg-brand-dark transition-colors flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Exportar
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Carteirado Total"
          value={totals.carteirado}
          icon={Briefcase}
          colorClass="from-blue-500/10 to-blue-600/5 border-blue-500/20 text-blue-400"
          subtitle={`${filteredJobs.length} jobs`}
        />
        <SummaryCard
          label="Faturado"
          value={totals.faturado}
          icon={DollarSign}
          colorClass="from-green-500/10 to-green-600/5 border-green-500/20 text-green-400"
          subtitle={`${((totals.faturado / totals.carteirado) * 100).toFixed(1)}% da carteira`}
        />
        <SummaryCard
          label="Custos Realizados"
          value={totals.custosRealizados}
          icon={TrendingDown}
          colorClass="from-orange-500/10 to-orange-600/5 border-orange-500/20 text-orange-400"
          subtitle="Custos já incorridos"
        />
        <SummaryCard
          label="Custos Futuros"
          value={totals.custosFuturos}
          icon={Calendar}
          colorClass="from-purple-500/10 to-purple-600/5 border-purple-500/20 text-purple-400"
          subtitle="Projeção de custos"
        />
      </div>

      {/* Filters */}
      <div className="bg-brand-dark-light/50 p-4 rounded-monumenta border border-gray-800/50 space-y-4">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-brand-gray" />
          <h3 className="font-semibold text-white">Filtros</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-brand-gray-light mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as JobStatus)}
              className="w-full bg-brand-dark border border-gray-800 text-white rounded-monumenta px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="todos">Todos</option>
              <option value="ativo">Ativo</option>
              <option value="concluido">Concluído</option>
              <option value="pausado">Pausado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-brand-gray-light mb-2">Cliente</label>
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="w-full bg-brand-dark border border-gray-800 text-white rounded-monumenta px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Todos os clientes</option>
              {uniqueClients.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm text-brand-gray-light mb-2">Buscar Job</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Nome, código ou cliente..."
              className="w-full bg-brand-dark border border-gray-800 text-white rounded-monumenta px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary placeholder:text-brand-gray"
            />
          </div>
        </div>

        {(searchText || clientFilter || statusFilter !== 'todos') && (
          <div className="text-sm text-brand-gray">
            Mostrando {filteredJobs.length} de {jobs.length} jobs
          </div>
        )}
      </div>

      {/* Jobs Table */}
      <div className="bg-brand-dark-light/50 rounded-monumenta border border-gray-800/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-dark border-b border-gray-800">
              <tr>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`px-4 py-3 text-xs font-medium text-brand-gray-light uppercase tracking-wider ${
                        header.column.getCanSort() ? 'cursor-pointer hover:bg-brand-dark-light' : ''
                      } transition-colors`}
                    >
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: ' ↑',
                          desc: ' ↓',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>
                    <FinancialEmptyState
                      variant="filter"
                      onAction={() => {
                        setSearchText('');
                        setClientFilter('');
                        setStatusFilter('todos');
                      }}
                    />
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`${
                      idx % 2 === 0 ? 'bg-brand-dark/50' : 'bg-brand-dark-light/30'
                    } hover:bg-brand-dark-light transition-colors cursor-pointer`}
                    onClick={() => {
                      // Navigate to job detail (to be implemented)
                      console.log('Navigate to job:', row.original.id);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {filteredJobs.length > 0 && (
              <tfoot className="bg-brand-primary/10 border-t-2 border-brand-primary">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-white font-bold">
                    TOTAL ({filteredJobs.length} jobs)
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-blue-400 font-bold">
                    {formatCurrency(totals.carteirado)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-green-400 font-bold">
                    {formatCurrency(totals.faturado)}
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-orange-400 font-bold">
                    {formatCurrency(totals.custosRealizados)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-purple-400 font-bold">
                    {formatCurrency(totals.custosFuturos)}
                  </td>
                  <td colSpan={2} className="px-4 py-3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="text-sm text-brand-gray text-center">
        Mostrando {filteredJobs.length} jobs • Última atualização: {new Date().toLocaleDateString('pt-BR')}
      </div>
    </div>
  );
}
